// tslint:disable:max-file-line-count
import { TActions } from '../../shared/common/state/actions';
import { ISerializableAppState } from '../../shared/common/state/app_state';
import { ECurrencyType, EPriceType } from '../../shared/json_query';

export const isWithPhotoEnabled = (state: ISerializableAppState) =>
  Boolean(state.filters.jsonQuery.wp && state.filters.jsonQuery.wp.value);

export const getPriceType = (state: ISerializableAppState): EPriceType =>
  (state.filters.jsonQuery.price_sm && state.filters.jsonQuery.price_sm.value && EPriceType.SM) || EPriceType.Total;

export const getMinPrice = (state: ISerializableAppState) =>
  state.filters.jsonQuery.price && state.filters.jsonQuery.price.value.gte;

export const getMaxArea = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.total_area && state.filters.jsonQuery.total_area.value.lte;

export const getMinArea = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.total_area && state.filters.jsonQuery.total_area.value.gte;

export const getMaxLandArea = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.site && state.filters.jsonQuery.site.value.lte;

export const getMinLandArea = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.site && state.filters.jsonQuery.site.value.gte;

export const getMaxFromMKAD = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.from_mcad_km && state.filters.jsonQuery.from_mcad_km.value.lte;

export const getMinFromMKAD = (state: ISerializableAppState): number | undefined =>
  state.filters.jsonQuery.from_mcad_km && state.filters.jsonQuery.from_mcad_km.value.gte;

export const getMaxPrice = (state: ISerializableAppState) =>
  state.filters.jsonQuery.price && state.filters.jsonQuery.price.value.lte;

export const getCurrencyType = (state: ISerializableAppState): ECurrencyType =>
  (state.filters.jsonQuery.currency && (state.filters.jsonQuery.currency.value as ECurrencyType)) ||
  ECurrencyType.Rouble;

export type TScheduledClick =
  | 'advanced'
  | 'currencyType'
  | 'search'
  | 'dealType'
  | 'offerType'
  | 'rooms'
  | 'map'
  | 'locationSwitcher'
  | 'priceType';

export class FastFilters {
  private state: ISerializableAppState;
  private minPrice: number | undefined;
  private maxPrice: number | undefined;
  private minArea: number | undefined;
  private maxArea: number | undefined;
  private minLandArea: number | undefined;
  private maxLandArea: number | undefined;
  private minFromMKAD: number | undefined;
  private maxFromMKAD: number | undefined;
  private withPhoto: boolean;
  private priceType: EPriceType;
  private geoSuggestValue: string;
  private geoSuggestFocus: boolean;
  private currencyType: ECurrencyType;

  private scheduledClick: TScheduledClick | undefined;
  private unscheduleCallback: (() => void) | undefined;

  public constructor(state: ISerializableAppState) {
    this.state = state;
    this.minPrice = getMinPrice(state);
    this.maxPrice = getMaxPrice(state);
    this.minArea = getMinArea(state);
    this.maxArea = getMaxArea(state);
    this.minFromMKAD = getMinFromMKAD(state);
    this.maxFromMKAD = getMaxFromMKAD(state);
    this.minLandArea = getMinLandArea(state);
    this.maxLandArea = getMaxLandArea(state);
    this.withPhoto = isWithPhotoEnabled(state);
    this.priceType = getPriceType(state);
    this.geoSuggestValue = '';
    this.geoSuggestFocus = false;
    this.currencyType = getCurrencyType(state);

    this.scheduledClick = undefined;
    this.unscheduleCallback = undefined;
  }

  public isWithPhotoEnabled() {
    return this.withPhoto;
  }

  public setWithPhoto = (withPhoto: boolean) => (this.withPhoto = withPhoto);

  public setPriceType = (priceType: EPriceType) => (this.priceType = priceType);

  public setMinPrice = (minPrice: number | undefined) => (this.minPrice = minPrice);

  public setMaxPrice = (maxPrice: number | undefined) => (this.maxPrice = maxPrice);

  public setMinArea = (minArea: number | undefined) => (this.minArea = minArea);

  public setMaxArea = (maxArea: number | undefined) => (this.maxArea = maxArea);

  public setMinFromMKAD = (minFromMKAD: number | undefined) => (this.minFromMKAD = minFromMKAD);

  public setMaxFromMKAD = (maxFromMKAD: number | undefined) => (this.maxFromMKAD = maxFromMKAD);

  public setMinLandArea = (minLandArea: number | undefined) => (this.minLandArea = minLandArea);

  public setMaxLandArea = (maxLandArea: number | undefined) => (this.maxLandArea = maxLandArea);

  public setGeoSuggestValue = (geoSuggestValue: string) => (this.geoSuggestValue = geoSuggestValue);

  public setGeoSuggestFocus = (geoSuggestFocus: boolean) => (this.geoSuggestFocus = geoSuggestFocus);

  public setCurrencyType = (currencyType: ECurrencyType) => (this.currencyType = currencyType);

  public scheduleClick = (scheduledClick: TScheduledClick, unscheduleCallback: () => void): void => {
    if (this.unscheduleCallback) {
      this.unscheduleCallback();
    }

    this.scheduledClick = scheduledClick;
    this.unscheduleCallback = unscheduleCallback;
  };

  public getActions(): TActions[] {
    const actions: TActions[] = [];

    if (this.priceType !== getPriceType(this.state)) {
      actions.push({
        type: 'filters/priceType/PRICE_TYPE_CHANGED',
        priceType: this.priceType,
      });
    }

    if (this.withPhoto !== isWithPhotoEnabled(this.state)) {
      actions.push({
        type: 'filters/with_photo/WITH_PHOTO_CHANGED',
        value: this.withPhoto,
      });
    }

    if (this.currencyType !== getCurrencyType(this.state)) {
      actions.push({
        type: 'filters/currencyType/CURRENCY_TYPE_CHANGED',
        currencyType: this.currencyType,
      });
    }

    if (this.minArea !== getMinArea(this.state)) {
      actions.push({
        type: 'filters/area/MIN_AREA_CHANGED',
        param: 'total_area',
        value: this.minArea,
        needTag: false,
      });
    }

    if (this.maxArea !== getMaxArea(this.state)) {
      actions.push({
        type: 'filters/area/MAX_AREA_CHANGED',
        param: 'total_area',
        value: this.maxArea,
        needTag: false,
      });
    }

    if (this.minFromMKAD !== getMinFromMKAD(this.state)) {
      actions.push({
        type: 'filters/from_mkad/MIN_DISTANCE_CHANGED',
        value: this.minFromMKAD,
      });
    }

    if (this.maxFromMKAD !== getMaxFromMKAD(this.state)) {
      actions.push({
        type: 'filters/from_mkad/MAX_DISTANCE_CHANGED',
        value: this.maxFromMKAD,
      });
    }

    if (this.minLandArea !== getMinLandArea(this.state)) {
      actions.push({
        type: 'filters/area/MIN_AREA_CHANGED',
        param: 'site_area',
        value: this.minLandArea,
        needTag: false,
      });
    }

    if (this.maxLandArea !== getMaxLandArea(this.state)) {
      actions.push({
        type: 'filters/area/MAX_AREA_CHANGED',
        param: 'site_area',
        value: this.maxLandArea,
        needTag: false,
      });
    }

    if (this.minPrice !== getMinPrice(this.state)) {
      actions.push({
        type: 'filters/price/MIN_PRICE_CHANGED',
        minPrice: this.minPrice,
      });
    }

    if (this.maxPrice !== getMaxPrice(this.state)) {
      actions.push({
        type: 'filters/price/MAX_PRICE_CHANGED',
        maxPrice: this.maxPrice,
      });
    }

    if (this.scheduledClick === 'advanced') {
      actions.push({
        type: 'filters/advanced/MODAL_OPENED',
      });
    }

    if (this.scheduledClick === 'search') {
      actions.push({
        type: 'filters/search/SEARCH_REQUEST_START_REQUESTED',
      });
    }

    if (this.scheduledClick === 'map') {
      actions.push({
        type: 'filters/map/MAP_OPENED',
      });
    }

    if (this.scheduledClick === 'locationSwitcher') {
      actions.push({
        type: 'filters/location_switcher/LOCATION_SWITCHER_OPEN_REQUESTED',
      });
    }

    if (this.scheduledClick === 'currencyType') {
      this.click('[data-mark=currencyType] button');
    }

    return actions;
  }

  public runSideeffects() {
    this.runGeoSuggestSideeffect();

    if (this.scheduledClick === 'dealType') {
      this.click('[data-mark=dealType]');
    }

    if (this.scheduledClick === 'offerType') {
      this.click('[data-mark=offerType]');
    }

    if (this.scheduledClick === 'priceType') {
      this.click('[data-mark=priceType] button');
    }

    if (this.scheduledClick === 'rooms') {
      this.click('[data-mark=rooms]');
    }
  }

  private runGeoSuggestSideeffect() {
    const node = document.querySelector('[data-mark=geoSuggest] input') as HTMLInputElement | null;

    if (!node) {
      return;
    }

    node.value = this.geoSuggestValue;
    node.dispatchEvent(
      new Event('input', {
        bubbles: true,
        cancelable: true,
      }),
    );

    if (this.geoSuggestFocus) {
      node.focus();
    }
  }

  private click = (pattern: string) => {
    const node = document.querySelector(pattern);

    if (!node) {
      return;
    }

    const event = document.createEvent('MouseEvents');
    event.initEvent('click', true, true);
    node.dispatchEvent(event);
  };
}
